import React, { useCallback, useState } from 'react';
import classNames from 'classnames';
import { CardMedia, Tile, TileContent, TileMedia } from '@one-thd/sui-atomic-components';
import { useImpression } from '@thd-olt-component-react/impression';
import propTypes from 'prop-types';

const {
  bool, func, number, oneOf, shape, string
} = propTypes;

export const MiniSwatch = ({
  swatchGuid,
  value,
  isSelected,
  enableHover,
  onClick,
  onMouseEnter,
  onMouseLeave,
  swatchItemId = null,
  index = 0,
  componentImpressionData = {
    name: '',
    component: '',
    position: 1,
    type: '',
    anchorSku: ''
  },
  isImpressionsRequired = false,
  wrapperClasses = null,
  swatchSize = '7',
  withoutTileBorder = false,
  ariaLabel = '',
  swatchName = ''
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isClickedMobile, setIsClickedMobile] = useState(false);

  const mouseEnter = useCallback((event) => {
    if (enableHover) {
      setIsHovered(true);
      onMouseEnter(event);
    }
  }, [enableHover, onMouseEnter]);

  const mouseLeave = useCallback((event) => {
    if (enableHover) {
      setIsHovered(false);
      onMouseLeave(event);
    }
  }, [enableHover, onMouseLeave]);

  const onClickMobile = () => {
    if (!enableHover) {
      setIsClickedMobile(true);
    }
  };

  const impressionProviderContextData = useImpression({
    data: {
      id: swatchItemId || '',
      name: componentImpressionData?.name || '',
      component: componentImpressionData?.component || '',
      position: componentImpressionData?.position || 1,
      type: componentImpressionData?.type || '',
      category: 'ssku',
      anchorSku: componentImpressionData?.anchorSku || ''
    }
  });

  // Safety check: Ensure impressionProviderContextData exists
  const impressionData = impressionProviderContextData || {};
  const shouldUseImpressionData = index !== 0 && isImpressionsRequired && (isHovered || isClickedMobile) && impressionData.ref;
  
  // Always use the same ref to prevent DOM manipulation issues
  const swatchRef = shouldUseImpressionData && impressionData.ref ? impressionData.ref : null;
  const swatchClickId = shouldUseImpressionData ? impressionData.clickID : null;

  const swatchCardClasses = classNames(
    `sui-w-${swatchSize} sui-h-${swatchSize} sui-rounded-md sui-border-solid sui-border-1 sui-border-[#fff]`,
    { 'sui-outline-2 sui-outline sui-outline-stronger hover:sui-bg-subtle focus-visible:sui-bg-subtle': isSelected },
    { 'hover:sui-outline hover:sui-outline-accent hover:sui-outline-2': !isSelected }
  );

  const SwatchBodyTile = () => (
    <Tile
      value={value}
      selected={isSelected}
      onMouseEnter={mouseEnter}
      onMouseLeave={mouseLeave}
      onClick={(event) => {
        onClickMobile();
        onClick(event);
      }}
      data-testid="mini-swatch-body-tile"
      data-clickid={swatchClickId || undefined}
      aria-label={
        ariaLabel || `${swatchName || "Swatch"} ${isSelected ? "selected" : "not selected"}`
      }
      ref={swatchRef}
    >
      <TileContent alignItems="center">
        <TileMedia
          alt={`${swatchName || "Swatch"} color or pattern variation`}
          src={swatchGuid}
          height={String(swatchSize)}
          key={`tile-media-${index}`}
        />
      </TileContent>
    </Tile>
  );

  const SwatchBodyCard = () => (
    <button
      type="button"
      data-testid="mini-swatch-body-card"
      data-clickid={swatchClickId || undefined}
      onMouseEnter={mouseEnter}
      onMouseLeave={mouseLeave}
      onClick={(event) => {
        onClickMobile();
        onClick(event);
      }}
      className={swatchCardClasses}
      value={value}
      aria-pressed={isSelected}
      aria-label={
        ariaLabel || `${swatchName || "Swatch"} ${isSelected ? "selected" : "not selected"}`
      }
      ref={swatchRef}
    >
      <CardMedia
        alt={`${swatchName || "Swatch"} color or pattern variation`}
        src={swatchGuid}
        width="min"
        aspect="square"
        className="sui-rounded-md sui-object-cover sui-aspect-square sui-w-min"
        key={`card-media-${index}`}
      />
    </button>
  );

  return (
    <div data-testid="mini-swatch-wrapper" className={wrapperClasses}>
      {withoutTileBorder ? <SwatchBodyCard /> : <SwatchBodyTile />}
    </div>
  );
};

MiniSwatch.displayName = 'MiniSwatch';

MiniSwatch.propTypes = {
  swatchGuid: string.isRequired,
  value: string.isRequired,
  isSelected: bool.isRequired,
  enableHover: bool.isRequired,
  onClick: func.isRequired,
  onMouseEnter: func.isRequired,
  onMouseLeave: func.isRequired,
  swatchItemId: string,
  index: number,
  componentImpressionData: shape({
    name: string,
    component: string,
    position: number,
    type: string,
    anchorSku: string
  }),
  isImpressionsRequired: bool,
  wrapperClasses: string,
  /** Default '7'; See Stencil Design System, Spacing Tokens for available pixel values */
  swatchSize: oneOf(['4', '5', '6', '7', '8', '9']),
  withoutTileBorder: bool,
  ariaLabel: string,
  swatchName: string
};
